import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    IDLE, PENDING, LIST, ADD, EDIT, ASC,
} from "const"
import { QidProduct } from "server/models"
import { StateType, deepCopy, hasAccess } from "client/utils"
import { qidProductsRoute } from "client/parts/routes"
import {
    PRM_QID_PRODUCT,
    StoreType,
    QidProductListProps,
    QidProductList,
    QidProductFilters,

    LoadAllQidCompanies,
    LoadAllQidProducts,
    LoadCompanyQidProducts,
    LoadDriveFolderByQidProductId,
    LoadDriveFolders,
    LoadDriveFiles,
    ChangeQidProductStatus,
    DeleteQidProduct,
    SortQidProducts,

    setQidCompanyList,
    setQidProductList,
    setQidProductIds,
    setQidProductSort,
    setQidProductFilters,
    addModal,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    Loader,
    Button,
    Table,
    TableSearchWrapper,
    SortRows,
    ChangeStatus,
    DeleteRow,
    BalloonMessage,
} from "client/components"

import AccessDenied from "client/parts/accessDenied"
import QidProductSearch from "client/pages/search/qidProductSearch"
import Columns from "./columns"


const List = ({
    companyId = null,
}: QidProductListProps) => {
    const productList = useSelector((state: StoreType) => state.qidProduct.productList)
    const productIds = useSelector((state: StoreType) => state.qidProduct.productIds)
    const sortColumn = useSelector((state: StoreType) => state.qidProduct.sortColumn)
    const sortOrder = useSelector((state: StoreType) => state.qidProduct.sortOrder)
    const filters = useSelector((state: StoreType) => state.qidProduct.filters)
    const reloadList = useSelector((state: StoreType) => state.qidProduct.reloadList)

    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    const editAccess = hasAccess(PRM_QID_PRODUCT, EDIT)
    const onlyActive = !editAccess

    useEffect(() => {
        if (!reloadList) return
        setLoadState(PENDING)
        // show only PUBLISHED rows for users without "edit access"
        if (companyId) {
            LoadCompanyQidProducts(companyId, onlyActive).then((res) => {
                dispatch(setQidProductList(res))
                setLoadState(IDLE)
            })
        } else {
            Promise.all([
                LoadAllQidCompanies(onlyActive).then((res) => { // used in search
                    dispatch(setQidCompanyList(res))
                }),
                LoadAllQidProducts(onlyActive).then((res) => {
                    dispatch(setQidProductList(res))
                }),
            ]).then(() => {
                setLoadState(IDLE)
            })
        }
    }, [reloadList])

    useEffect(() => {
        const sortedIds = SortQidProducts(Object.values(productList), sortColumn, sortOrder, filters)
        dispatch(setQidProductIds(sortedIds))
    }, [productList, sortColumn, sortOrder, filters])

    const onSort: SortRows = (column: keyof QidProduct, order) => {
        dispatch(setQidProductSort(column, order))
    }

    const onFilter = (newFilters: QidProductFilters) => {
        dispatch(setQidProductFilters(newFilters))
    }

    const onStatusChange: ChangeStatus = (product: QidProduct, callback) => {
        ChangeQidProductStatus(product).then((status) => {
            callback(() => {
                const productListCopy: QidProductList = deepCopy(productList)
                productListCopy[product.id] = {
                    ...productListCopy[product.id],
                    status,
                }
                dispatch(setQidProductList(productListCopy))
            })
        })
    }

    const onDelete: DeleteRow = (product: QidProduct, callback) => {
        LoadDriveFolderByQidProductId(product.id).then((productFolder) => {
            LoadDriveFolders(userData.accountId, productFolder.id)
            .then((folders) => {
                LoadDriveFiles(userData.accountId, productFolder.id, "id", ASC, 0, 10)
                .then((files) => {
                    // product can not be removed if its folder is not empty
                    if (folders.length || files.length) {
                        BalloonMessage(lang.productFolderNotEmpty)
                        callback(false, null)
                        return
                    }

                    DeleteQidProduct(product.id).then((removedRows) => {
                        // product folder will be removed automatically
                        const success = removedRows === 1
                        callback(success, () => {
                            const productListCopy: QidProductList = productList ? deepCopy(productList) : {}
                            delete productListCopy[product.id]
                            dispatch(setQidProductList(productListCopy))
                            dispatch(setQidProductIds(productIds.filter((id) => id !== product.id)))
                        })
                    })
                })
            })
        })
    }

    const shortView = onlyActive && companyId !== null // show only "logo" and "name" for account users when "companyId" is not null
    const columns = Columns(shortView)

    const listAccess = hasAccess(PRM_QID_PRODUCT, LIST)
    const addAccess = hasAccess(PRM_QID_PRODUCT, ADD)

    if (!listAccess) return <AccessDenied />
    return (
        <Container>
            {
                !companyId && ( // show in tab mode when "companyId" is not null
                    <PageHeader>
                        <PageTitle>{lang.products}</PageTitle>
                    </PageHeader>
                )
            }
            <PageContent>
                {
                    loadState === PENDING
                    && <Loader />
                }
                <TableSearchWrapper>
                    <QidProductSearch
                        id="productList"
                        rightOffset={addAccess}
                        shortView={shortView}
                        filters={filters}
                        onFilter={onFilter}
                    />
                    {
                        addAccess && (
                            <Button
                                variant="success"
                                onClick={() => dispatch(addModal({ url: `${qidProductsRoute}/${EDIT}/0` }))}
                            >
                                {lang.add}
                            </Button>
                        )
                    }
                </TableSearchWrapper>
                <Table
                    columns={columns}
                    dataList={productList}
                    dataIds={productIds}
                    sortColumn={sortColumn}
                    sortOrder={sortOrder}
                    rowSize="md"
                    showPagination
                    onSort={onSort}
                    onStatusChange={onStatusChange}
                    onDelete={onDelete}
                />
            </PageContent>
        </Container>
    )
}
export default List
